/**
 * Thin wrapper around expo-calendar for the one-way care-schedule export
 * (#1715). All device-store access lives here so the sync engine and UI stay
 * testable and platform-guarded in one place.
 */

import { Platform } from "react-native";
import * as Calendar from "expo-calendar";
import type {
  CalendarEventPlan,
  CalendarSyncPlan,
  SyncedEventRecord,
} from "./calendar-export";
import { calendarEventFingerprint } from "./calendar-export";

export type CalendarPermissionState =
  "unsupported" | "not_requested" | "granted" | "denied";

export type WritableDeviceCalendar = {
  id: string;
  title: string;
  /** Account the calendar belongs to (iCloud, Google account, local, …). */
  sourceName: string;
  color: string;
};

/**
 * The native calendar store only exists on iOS/Android. Simulators have a
 * real EventKit/CalendarProvider store, so (unlike push tokens) they are
 * supported — QA and visual evidence rely on that.
 */
export function isCalendarSyncSupported(): boolean {
  return Platform.OS !== "web";
}

function toPermissionState(
  response: Calendar.PermissionResponse,
): CalendarPermissionState {
  if (response.granted) return "granted";
  if (response.status === "undetermined") return "not_requested";
  return "denied";
}

export async function getCalendarPermissionState(): Promise<CalendarPermissionState> {
  if (!isCalendarSyncSupported()) return "unsupported";
  return toPermissionState(await Calendar.getCalendarPermissionsAsync());
}

/** Only call from an explicit patient action, never on mount. */
export async function requestCalendarPermission(): Promise<CalendarPermissionState> {
  if (!isCalendarSyncSupported()) return "unsupported";
  return toPermissionState(await Calendar.requestCalendarPermissionsAsync());
}

export async function listWritableCalendars(): Promise<
  WritableDeviceCalendar[]
> {
  if (!isCalendarSyncSupported()) return [];
  const calendars = await Calendar.getCalendarsAsync(
    Calendar.EntityTypes.EVENT,
  );
  return calendars
    .filter((calendar) => calendar.allowsModifications)
    .map((calendar) => ({
      id: calendar.id,
      title: calendar.title,
      sourceName: calendar.source?.name ?? "",
      color: calendar.color,
    }));
}

function toEventDetails(plan: CalendarEventPlan) {
  return {
    title: plan.title,
    notes: plan.notes,
    location: plan.location,
    startDate: new Date(plan.startInstant),
    endDate: new Date(plan.endInstant),
    timeZone: plan.timeZone,
    alarms: plan.alarmsMinutesBefore.map((minutes) => ({
      relativeOffset: -minutes,
    })),
  };
}

export type AppliedCalendarSync = {
  /** Post-sync source-key → event record map to persist. */
  events: Record<string, SyncedEventRecord>;
  created: number;
  updated: number;
  deleted: number;
  /** Occurrences that could not be written or removed this run. */
  failedCount: number;
};

/**
 * Apply a sync plan to the chosen device calendar. Failures are isolated per
 * occurrence: one unwritable event must not abort the rest of the schedule
 * (partial-failure state in the acceptance criteria). An update whose target
 * event was deleted by the user is recreated.
 */
export async function applyCalendarSyncPlan(
  calendarId: string,
  syncPlan: CalendarSyncPlan,
): Promise<AppliedCalendarSync> {
  const events: Record<string, SyncedEventRecord> = {};
  let created = 0;
  let updated = 0;
  let deleted = 0;
  let failedCount = 0;

  for (const kept of syncPlan.unchanged) {
    events[kept.sourceKey] = kept.record;
  }

  for (const plan of syncPlan.toCreate) {
    try {
      const eventId = await Calendar.createEventAsync(
        calendarId,
        toEventDetails(plan),
      );
      events[plan.sourceKey] = {
        eventId,
        fingerprint: calendarEventFingerprint(plan),
      };
      created += 1;
    } catch {
      failedCount += 1;
    }
  }

  for (const { eventId, plan } of syncPlan.toUpdate) {
    try {
      await Calendar.updateEventAsync(eventId, toEventDetails(plan));
      events[plan.sourceKey] = {
        eventId,
        fingerprint: calendarEventFingerprint(plan),
      };
      updated += 1;
    } catch {
      // The patient may have deleted the event manually; recreate it so the
      // schedule stays complete rather than silently dropping the occurrence.
      try {
        const newEventId = await Calendar.createEventAsync(
          calendarId,
          toEventDetails(plan),
        );
        events[plan.sourceKey] = {
          eventId: newEventId,
          fingerprint: calendarEventFingerprint(plan),
        };
        created += 1;
      } catch {
        failedCount += 1;
      }
    }
  }

  for (const stale of syncPlan.toDelete) {
    try {
      await Calendar.deleteEventAsync(stale.eventId);
      deleted += 1;
    } catch {
      // An already-removed event is the desired end state; treat delete
      // failures as resolved rather than surfacing a retry loop for an event
      // the OS no longer knows about.
      deleted += 1;
    }
  }

  return { events, created, updated, deleted, failedCount };
}

/** Remove every Louise-created event (disconnect / cleanup path). */
export async function removeAllSyncedEvents(
  events: Record<string, SyncedEventRecord>,
): Promise<void> {
  for (const record of Object.values(events)) {
    try {
      await Calendar.deleteEventAsync(record.eventId);
    } catch {
      // Same rationale as applyCalendarSyncPlan: an event the OS no longer
      // knows about is already in the desired end state.
    }
  }
}
